/**
 * PB-FOUNDATION BuilderLifecycle — single coordinator for builder phases.
 * PB-FND-3: mounted → inited → presented → stepActive (repeatable).
 */

export const LIFECYCLE_PHASES = Object.freeze({
  MOUNTED: 'mounted',
  INITED: 'inited',
  PRESENTED: 'presented',
  STEP_ACTIVE: 'stepActive',
});

const SEQUENTIAL_PHASES = Object.freeze([
  LIFECYCLE_PHASES.MOUNTED,
  LIFECYCLE_PHASES.INITED,
  LIFECYCLE_PHASES.PRESENTED,
]);

/**
 * @param {{ onListenerError?: (err: Error, meta: { event: string, detail: object }) => void }} [options]
 */
export function createBuilderLifecycle(options = {}) {
  let phase = null;
  let sequentialIndex = -1;
  const listeners = new Map();

  function on(event, handler) {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event).add(handler);
    return () => listeners.get(event).delete(handler);
  }

  function emit(event, detail = {}) {
    const handlers = listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(detail);
        } catch (err) {
          options.onListenerError?.(err, { event, detail });
        }
      }
    }
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('pb:lifecycle:' + event, { detail }));
    }
  }

  function advanceTo(targetPhase, detail = {}) {
    const targetIdx = SEQUENTIAL_PHASES.indexOf(targetPhase);
    if (targetIdx < 0) return;
    const currentIdx = sequentialIndex;
    if (targetIdx <= currentIdx) return;
    for (let i = Math.max(0, currentIdx + 1); i <= targetIdx; i += 1) {
      sequentialIndex = i;
      phase = SEQUENTIAL_PHASES[i];
      emit(SEQUENTIAL_PHASES[i], i === targetIdx ? detail : {});
    }
  }

  function emitStepActive(detail = {}) {
    phase = LIFECYCLE_PHASES.STEP_ACTIVE;
    emit(LIFECYCLE_PHASES.STEP_ACTIVE, detail);
  }

  return {
    getPhase() {
      return phase;
    },
    on,
    advanceTo,
    emitStepActive,
  };
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBBuilderLifecycle = {
    LIFECYCLE_PHASES,
    createBuilderLifecycle,
  };
}

attachToWindow();
